import * as React from 'react';

import * as SheetPrimitive from '@radix-ui/react-dialog';
import { type VariantProps, cva } from 'class-variance-authority';
import { X } from 'lucide-react';

import { cn } from '../lib/utils';

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      'vai-fixed vai-inset-0 vai-z-[9999] vai-bg-black/80 vai- data-[state=open]:vai-animate-in data-[state=closed]:vai-animate-out data-[state=closed]:vai-fade-out-0 data-[state=open]:vai-fade-in-0',
      className,
    )}
    {...props}
    ref={ref}
  />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva(
  'vai-fixed vai-z-[9999] vai-gap-4 vai-bg-background vai-shadow-lg vai-transition vai-ease-in-out data-[state=open]:vai-animate-in data-[state=closed]:vai-animate-out data-[state=closed]:vai-duration-300 data-[state=open]:vai-duration-500',
  {
    variants: {
      side: {
        top: 'vai-inset-x-0 vai-top-0 vai-border-b data-[state=closed]:vai-slide-out-to-top data-[state=open]:vai-slide-in-from-top',
        bottom:
          'vai-inset-x-0 vai-bottom-0 vai-border-t data-[state=closed]:vai-slide-out-to-bottom data-[state=open]:vai-slide-in-from-bottom',
        left: 'vai-inset-y-0 vai-left-0 vai-h-full vai-w-3/4 vai-border-r data-[state=closed]:vai-slide-out-to-left data-[state=open]:vai-slide-in-from-left sm:vai-max-w-sm',
        right:
          'vai-inset-y-0 vai-right-0 vai-h-full vai-w-3/4 vai- vai-border-l data-[state=closed]:vai-slide-out-to-right data-[state=open]:vai-slide-in-from-right sm:vai-max-w-sm',
      },
    },
    defaultVariants: {
      side: 'right',
    },
  },
);

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(({ side = 'right', className, children, ...props }, ref) => (
  <SheetPortal>
    <SheetOverlay className="!vai-bg-black/50" />
    <SheetPrimitive.Content
      ref={ref}
      className={cn(sheetVariants({ side }), className)}
      {...props}
    >
      {children}
      <SheetPrimitive.Close className="vai-cursor-pointer vai-bg-transparent vai-border-none vai-absolute vai-right-4 vai-top-4 vai-rounded-sm vai-opacity-70 vai-ring-offset-background vai-transition-opacity hover:vai-opacity-100 focus:vai-outline-none focus:vai-ring-2 disabled:vai-pointer-events-none data-[state=open]:vai-bg-secondary">
        <X className="vai-h-4 vai-w-4 " />
        <span className="vai-sr-only">Close</span>
      </SheetPrimitive.Close>
    </SheetPrimitive.Content>
  </SheetPortal>
));
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'vai-flex vai-flex-col vai-space-y-2 vai-text-center sm:vai-text-left',
      className,
    )}
    {...props}
  />
);
SheetHeader.displayName = 'SheetHeader';

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'vai-flex vai-flex-col-reverse sm:vai-flex-row sm:vai-justify-between sm:vai-space-x-2',
      className,
    )}
    {...props}
  />
);
SheetFooter.displayName = 'SheetFooter';

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn(
      'vai-text-lg vai-font-semibold vai-text-foreground',
      className,
    )}
    {...props}
  />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn('vai-text-sm vai-text-muted-foreground', className)}
    {...props}
  />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
